import React, { useState } from 'react';
import Breadcrumb from '../../components/common/Breadcrumb';
import { PageLoader } from '../../components/common/Loader';
import { IconArchive, IconFile, IconSearch } from '@tabler/icons-react';
import { useArchivedDocuments } from '../../hooks/useDocuments';

function formatDate(d) {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

const CATEGORY_LABELS = {
    cv_resume: 'CV / Résumé', cover_letter: 'Cover Letter', certificate: 'Certificate',
    id_document: 'ID Document', contract: 'Contract', appraisal: 'Appraisal',
    training: 'Training', office_memo: 'Office Memo', policy: 'Policy',
    personal: 'Personal', other: 'Other',
};

export default function ArchivedDocuments() {
    const [search, setSearch] = useState('');
    const { data, isLoading } = useArchivedDocuments();
    const docs = data?.documents || [];

    const filtered = search.trim()
        ? docs.filter(d =>
            d.title?.toLowerCase().includes(search.toLowerCase()) ||
            d.archived_reason?.toLowerCase().includes(search.toLowerCase())
          )
        : docs;

    if (isLoading) return <PageLoader text="Loading archived documents..." />;

    return (
        <div className="flex flex-col gap-20 pb-30">
            <Breadcrumb title="Archived Documents" path="Documents" />

            <div className="form-group relative">
                <IconSearch size={13} className="absolute top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none" style={{ left: '10px' }} />
                <input
                    type="text"
                    placeholder="Search archived documents..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    style={{ paddingLeft: '28px' }}
                />
            </div>

            {filtered.length === 0 ? (
                <div className="bg-bg-dark rounded-lg border border-border shadow-light py-60 text-center">
                    <div className="w-44 h-44 rounded-full bg-bg-light flex items-center justify-center mx-auto mb-12">
                        <IconArchive className="text-text-secondary" size={20} />
                    </div>
                    <p className="text-sm font-semibold text-text-primary mb-4">No archived documents</p>
                    <p className="text-[11px] text-text-secondary">Documents from departed employees will appear here.</p>
                </div>
            ) : (
                <div className="bg-bg-dark rounded-lg border border-border shadow-light overflow-hidden">
                    <div className="px-20 py-12 border-b border-border bg-bg-light/30 flex items-center justify-between">
                        <h3 className="text-[13px] font-semibold text-text-primary">Archived Documents</h3>
                        <span className="text-[11px] text-text-secondary">{filtered.length} document{filtered.length !== 1 ? 's' : ''}</span>
                    </div>
                    {filtered.map((doc, i) => (
                        <div key={doc.id} className={`flex items-start gap-12 px-15 py-10 ${i > 0 ? 'border-t border-border' : ''}`}>
                            <div className="w-[34px] h-[34px] rounded-lg bg-bg-light flex items-center justify-center flex-shrink-0 mt-1">
                                <IconFile size={15} className="text-text-secondary" />
                            </div>
                            <div className="flex-grow min-w-0">
                                <p className="text-[13px] font-semibold text-text-primary truncate">{doc.title}</p>
                                <div className="flex flex-wrap items-center gap-8 mt-2">
                                    <span className="text-[10px] text-text-secondary">
                                        {CATEGORY_LABELS[doc.category] || doc.category}
                                    </span>
                                    <span className="text-text-secondary/30">·</span>
                                    <span className="text-[10px] text-text-secondary">
                                        Archived {formatDate(doc.archived_at)}
                                    </span>
                                </div>
                                {doc.archived_reason && (
                                    <p className="text-[11px] text-text-secondary mt-4 italic">{doc.archived_reason}</p>
                                )}
                            </div>
                            <span className="text-[9px] font-bold uppercase tracking-widest px-6 py-2 rounded-full bg-bg-light text-text-secondary flex-shrink-0 mt-1">
                                Archived
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
